import React from 'react';
import { 
  Home, 
  User, 
  Shield, 
  FileText, 
  CreditCard, 
  Calculator,
  MessageSquare,
  Settings,
  HelpCircle,
  Bot,
  Sparkles,
  BarChart3,
  ExternalLink,
  BookOpen,
  Scale,
  DollarSign,
  Heart
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useNavigate, useLocation } from 'react-router-dom';
import MAILogo from './ui/MAILogo';

const navigationItems = [
  {
    section: "Dashboard",
    items: [
      { icon: Home, label: "Overview", href: "/dashboard", key: "dashboard" },
      { icon: MAILogo, label: "MILCONNECT AI", href: "/milconnect-ai", badge: "New", key: "milconnect-ai", action: "openChat" },
    ]
  },
  {
    section: "Personal Information",
    items: [
      { icon: User, label: "Profile", href: "/profile", key: "profile" },
      { icon: Shield, label: "Security Clearance", href: "/security", key: "security" },
      { icon: CreditCard, label: "ID Card Services", href: "/id-services", key: "id-services" },
    ]
  },
  {
    section: "Benefits & Services",
    items: [
      { icon: FileText, label: "Benefits Overview", href: "/benefits", aiEnabled: true, key: "benefits" },
      { icon: Calculator, label: "Benefits Calculators", href: "/calculators", key: "calculators" },
      { icon: FileText, label: "Document Center", href: "/documents", aiEnabled: true, key: "documents" },
    ]
  },
  {
    section: "Veterans Resources",
    items: [
      { icon: BookOpen, label: "All Resources", href: "/resources", key: "resources" },
      { icon: Scale, label: "VA Disability & Claims", href: "/resources?cat=disability", key: "resources-disability" },
      { icon: DollarSign, label: "Pay & Retirement", href: "/resources?cat=pay-retirement", key: "resources-pay" },
      { icon: Heart, label: "Family & Health", href: "/resources?cat=family-health", key: "resources-family" },
    ]
  },
  {
    section: "External Services",
    items: [
      { icon: Shield, label: "Join MILCONNECT", href: "https://www.milconnect.com", key: "join-milconnect", external: true },
    ]
  },
  {
    section: "Account",
    items: [
      { icon: Settings, label: "Settings", href: "/settings", key: "settings" },
      { icon: HelpCircle, label: "Help & Support", href: "/help", key: "help" },
    ]
  }
];

const Sidebar = ({ isOpen, onClose, onOpenChat }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (item) => {
    if (item.action === 'openChat' && onOpenChat) {
      onOpenChat(); // Open the chat instead of navigating
      onClose(); // Close sidebar on mobile
    } else if (item.external) {
      window.open(item.href, '_blank'); // Open external links in new window
      onClose(); // Close sidebar on mobile
    } else {
      navigate(item.href);
      onClose(); // Close sidebar on mobile after navigation
    }
  };

  const isActive = (href, key) => {
    if (href === '/dashboard' || href === '/') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    if (key?.startsWith('resources')) {
      return location.pathname === '/resources' && (href.includes('?') ? location.search === href.split('?')[1] : !location.search);
    }
    return location.pathname === href;
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-30
        w-72 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900
        border-r border-slate-700 shadow-2xl
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          
          {/* Sidebar Content */}
          <div className="flex-1 overflow-y-auto py-6">
            <div className="px-6 space-y-8">
              {navigationItems.map((section) => (
                <div key={section.section}>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                    {section.section}
                  </h3>
                  <nav className="space-y-1">
                    {section.items.map((item) => (
                      <Button
                        key={item.key}
                        variant={isActive(item.href, item.key) ? "secondary" : "ghost"}
                        className={`
                          w-full justify-start text-left h-10 transition-all duration-200
                          ${isActive(item.href, item.key)
                            ? 'bg-blue-600/20 text-blue-300 border-l-2 border-blue-400 shadow-lg' 
                            : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                          }
                          ${item.aiEnabled ? 'group relative' : ''}
                        `}
                        onClick={() => handleNavigation(item)}
                      >
                        <item.icon className={`mr-3 h-4 w-4 ${
                          isActive(item.href, item.key) ? 'text-blue-400' : ''
                        }`} />
                        <span className="flex-1">{item.label}</span>
                        
                        {item.external && (
                          <ExternalLink className="h-3 w-3 text-gray-400 ml-2" />
                        )}
                        
                        {item.aiEnabled && (
                          <div className="ml-2 opacity-60 group-hover:opacity-100 transition-opacity">
                            <Sparkles className="h-3 w-3 text-emerald-400" />
                          </div>
                        )}
                        
                        {item.badge && (
                          <Badge 
                            variant={item.badge === "New" ? "default" : "secondary"}
                            className={`ml-2 text-xs ${
                              item.badge === "New" 
                                ? "bg-emerald-500 text-white" 
                                : item.badge === "24/7"
                                ? "bg-blue-500 text-white"
                                : ""
                            }`}
                          >
                            {item.badge}
                          </Badge>
                        )}
                      </Button>
                    ))}
                  </nav>
                </div>
              ))}
            </div>
          </div>

          {/* AI Status Footer */}
          <div className="p-6 border-t border-slate-700">
            <div className="bg-gradient-to-r from-blue-600/20 to-emerald-600/20 rounded-lg p-4 border border-blue-500/30">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full flex items-center justify-center">
                  <MAILogo className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">AI Assistant</p>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-xs text-slate-300">Online & Ready</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Your AI assistant has processed <span className="text-emerald-400 font-medium">127 documents</span> and identified <span className="text-blue-400 font-medium">3 benefit optimizations</span>.
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;